import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";

const SLIDE_MS = 250;

/**
 * Right-side slide-in panel for Masters screens (details / mapping of a grid row).
 * Children render inside a form; onSubmit(event, close) gets the prevented submit event
 * and a `close` callback that slides the panel out. Save and close sit in the header so
 * they stay in reach however tall the panel is. Mount it to open; onClose runs after the
 * slide-out. Esc closes; clicking outside does not, so unsaved changes are not lost.
 * size="wide": width follows the window (75vw up to 1200px; full width below 768px) instead of `width`.
 */
export default function MasterDrawer({
  title,
  subtitle,
  width = "640px",
  size,
  busy = false,
  saveText = "Save",
  showSave = true,
  saveDisabled = false,
  onSubmit,
  onClose,
  children,
}) {
  const [open, setOpen] = useState(true);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const requestClose = () => {
    if (!open) return;
    setOpen(false);
    timer.current = setTimeout(onClose, SLIDE_MS);
  };

  const handleSubmit = (e) => {
    // React passes submits of forms nested in portals (a popup opened from inside the drawer) up to this form;
    // only this drawer's own form saves the drawer.
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    if (!busy && !saveDisabled && onSubmit) onSubmit(e, requestClose);
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={open}
      closeTimeoutMS={SLIDE_MS}
      onRequestClose={requestClose}
      shouldCloseOnOverlayClick={false}
      contentLabel={title}
      overlayClassName={{
        base: "mst-drawer-overlay",
        afterOpen: "is-open",
        beforeClose: "is-closing",
      }}
      className={{
        base: `mst-drawer${size ? ` mst-drawer--${size}` : ""}`,
        afterOpen: "is-open",
        beforeClose: "is-closing",
      }}
      // A size class (CSS, follows the window) wins over the fixed width.
      style={{ content: size ? {} : { width } }}
    >
      <form onSubmit={handleSubmit} className={busy ? "is-busy" : ""} noValidate>
        <div className="mst-drawer__header">
          <div className="mst-drawer__titles">
            <h6 className="mst-drawer__title">{title}</h6>
            {subtitle && <div className="mst-drawer__subtitle">{subtitle}</div>}
          </div>
          <div className="mst-drawer__actions">
            {showSave && (
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy || saveDisabled}>
                {busy ? (
                  <span>
                    <i className="fas fa-spinner fa-spin" /> Saving
                  </span>
                ) : (
                  <span>
                    <i className="fas fa-check" /> {saveText}
                  </span>
                )}
              </button>
            )}
            <button type="button" className="close" aria-label="Close" title="Close (Esc)" onClick={requestClose}>
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
        <div className="mst-drawer__body">{children}</div>
      </form>
    </Modal>
  );
}
